/**
 * Smart Animate (audit L6 subset): when a screen transition is `smart`,
 * elements matched between the outgoing and incoming screens morph — their
 * boxes lerp while their snapshots crossfade — and unmatched elements fade
 * out/in. Matching is heuristic (no authoring metadata): same-type buckets
 * paired in frame-relative reading order, standalone text by content first.
 * ponytail: appearance is snapshot-crossfade — angle/color/shape changes
 * blend rather than geometrically interpolate; per-channel FLIP needs
 * override-driven rendering if this ever falls visually short.
 */

export type Box = { x: number; y: number; w: number; h: number };

export type SmartItem =
  | {
      kind: "pair";
      fromImg: CanvasImageSource;
      toImg: CanvasImageSource;
      from: Box;
      to: Box;
      /** geometric FLIP: snapshots are rendered angle-0 and the compositor
       * rotates the lerped box by the lerped angle (radians) */
      fromAngle?: number;
      toAngle?: number;
    }
  | { kind: "exit"; img: CanvasImageSource; box: Box }
  | { kind: "enter"; img: CanvasImageSource; box: Box };

export const lerpBox = (a: Box, b: Box, p: number): Box => ({
  x: a.x + (b.x - a.x) * p,
  y: a.y + (b.y - a.y) * p,
  w: a.w + (b.w - a.w) * p,
  h: a.h + (b.h - a.h) * p,
});

type Matchable = {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  containerId?: string | null;
  text?: string;
  // weak-type-safe: excalidraw's customData is Record-shaped per element type
  customData?: Readonly<Record<string, unknown>> | null;
};

const matchKeyOf = (el: Matchable): string | null => {
  const key = el.customData?.matchKey;
  return typeof key === "string" && key ? key : null;
};

const FRAME_TYPES = new Set(["frame", "magicframe"]);

/** Pairs elements across two screens. Bound-text children (they ride inside
 * their container's snapshot) and frame-likes never match. */
export const matchElements = <T extends Matchable>(
  fromEls: readonly T[],
  fromOrigin: { x: number; y: number },
  toEls: readonly T[],
  toOrigin: { x: number; y: number },
): { pairs: [T, T][]; exits: T[]; enters: T[] } => {
  const eligible = (els: readonly T[]) =>
    els.filter((el) => !el.containerId && !FRAME_TYPES.has(el.type));
  const byOrder = (origin: { x: number; y: number }) => (a: T, b: T) =>
    a.y - origin.y - (b.y - origin.y) || a.x - origin.x - (b.x - origin.x);

  const pairs: [T, T][] = [];

  // explicit match keys pair first — across type buckets, beating every
  // heuristic (customData survives copy/paste, so keys ride duplication)
  let fromPool = eligible(fromEls);
  let toPool = eligible(toEls);
  for (const a of [...fromPool]) {
    const key = matchKeyOf(a);
    if (!key) {
      continue;
    }
    const b = toPool.find((t) => matchKeyOf(t) === key);
    if (b) {
      pairs.push([a, b]);
      fromPool = fromPool.filter((e) => e !== a);
      toPool = toPool.filter((e) => e !== b);
    }
  }

  const buckets = new Map<string, { from: T[]; to: T[] }>();
  for (const el of fromPool) {
    const bucket = buckets.get(el.type) ?? { from: [], to: [] };
    bucket.from.push(el);
    buckets.set(el.type, bucket);
  }
  for (const el of toPool) {
    const bucket = buckets.get(el.type) ?? { from: [], to: [] };
    bucket.to.push(el);
    buckets.set(el.type, bucket);
  }

  const exits: T[] = [];
  const enters: T[] = [];
  for (const [type, bucket] of buckets) {
    let from = [...bucket.from].sort(byOrder(fromOrigin));
    let to = [...bucket.to].sort(byOrder(toOrigin));
    if (type === "text") {
      // exact-content pass first: renamed positions must still track
      for (const a of [...from]) {
        const b = to.find((t) => t.text !== undefined && t.text === a.text);
        if (b) {
          pairs.push([a, b]);
          from = from.filter((e) => e !== a);
          to = to.filter((e) => e !== b);
        }
      }
    }
    const n = Math.min(from.length, to.length);
    for (let i = 0; i < n; i++) {
      pairs.push([from[i], to[i]]);
    }
    exits.push(...from.slice(n));
    enters.push(...to.slice(n));
  }
  return { pairs, exits, enters };
};

/** Draws one smart-transition frame: pairs at lerped boxes with crossfade,
 * exits fading out, enters fading in. Restores globalAlpha. */
export const drawSmartFrame = (
  ctx: CanvasRenderingContext2D,
  items: readonly SmartItem[],
  progress: number,
): void => {
  const p = Math.min(1, Math.max(0, progress));
  for (const item of items) {
    if (item.kind === "pair") {
      const box = lerpBox(item.from, item.to, p);
      if (item.fromAngle !== undefined || item.toAngle !== undefined) {
        // geometric FLIP: rotate the lerped box around its center by the
        // shortest-path lerped angle (snapshots are rendered angle-0)
        const a0 = item.fromAngle ?? 0;
        let delta = (item.toAngle ?? 0) - a0;
        if (delta > Math.PI) {
          delta -= Math.PI * 2;
        } else if (delta < -Math.PI) {
          delta += Math.PI * 2;
        }
        ctx.save();
        ctx.translate(box.x + box.w / 2, box.y + box.h / 2);
        ctx.rotate(a0 + delta * p);
        ctx.globalAlpha = 1 - p;
        ctx.drawImage(item.fromImg, -box.w / 2, -box.h / 2, box.w, box.h);
        ctx.globalAlpha = p;
        ctx.drawImage(item.toImg, -box.w / 2, -box.h / 2, box.w, box.h);
        ctx.restore();
        continue;
      }
      ctx.globalAlpha = 1 - p;
      ctx.drawImage(item.fromImg, box.x, box.y, box.w, box.h);
      ctx.globalAlpha = p;
      ctx.drawImage(item.toImg, box.x, box.y, box.w, box.h);
    } else {
      ctx.globalAlpha = item.kind === "exit" ? 1 - p : p;
      ctx.drawImage(item.img, item.box.x, item.box.y, item.box.w, item.box.h);
    }
  }
  ctx.globalAlpha = 1;
};
